import { Classes, Dialog, InputGroup } from "@blueprintjs/core";
import { Form, Formik } from "formik";
import { observer } from "mobx-react";
import { StandardFormInput } from "modules/common/components/form/StandardFormInput";
import { Page } from "modules/common/components/page/Page";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";
import { routes } from "modules/common/routes";
import { AppLogo } from "modules/root/components/logo/AppLogo";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import authStyles from "../auth/components/AuthDialog.module.scss";
import pageStyles from "../auth/components/AuthPage.module.scss";
import { AccessLinkPage, SessionType } from "../auth/models/session";
import { inviteSchema } from "./invite-rules";
import styles from "./InvitePage.module.scss";

interface InviteValues {
  login: string;
  password: string;
  repeat: string;
}

/**
 * Страница по ссылке из письма: приглашение (придумать логин и пароль) или смена пароля.
 * Доступна без входа. Окно — как окно входа, поля — как в остальных формах приложения.
 */
export const InvitePage = observer(({ session }: { session: SessionType }) => {
  const location = useLocation();
  const navigate = useStableNavigate();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") ?? "", [location.search]);

  const [page, setPage] = useState<AccessLinkPage | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (!token) {
        setProblem("В ссылке нет кода. Откройте ссылку из письма целиком.");
        return;
      }

      const result = await session.checkAccessLink(token);
      if (!alive) {
        return;
      }

      if (typeof result === "string") {
        setProblem(result);
      } else {
        setPage(result);
      }
    })();

    return () => {
      alive = false;
    };
  }, [session, token]);

  const invite = page?.purpose === "invite";

  const schema = useMemo(
    () => (page ? inviteSchema(invite, page.loginMin, page.loginMax, page.passwordMin) : null),
    [page, invite]
  );

  return (
    <Page className={pageStyles.page}>
      {/* то же окно, что и вход: логотип сверху, форма под ним, закрыть нельзя */}
      <Dialog
        isOpen={true}
        canOutsideClickClose={false}
        canEscapeKeyClose={false}
        backdropClassName={authStyles.plainBackdrop}
        className={`figma-dialog ${authStyles.dialog} ${styles.dialog}`}
      >
        <div className={authStyles.header}>
          <AppLogo className={authStyles.logo} />
          {page && <div className={styles.title}>{invite ? "Добро пожаловать!" : "Новый пароль"}</div>}
          {page && (
            <div className={styles.text}>
              {page.name}, {invite ? "придумайте логин и пароль для входа." : "задайте новый пароль для входа."}
            </div>
          )}
          {!page && !problem && <div className={styles.text}>Проверяем ссылку…</div>}
          {problem && <div className={styles.title}>Ссылка не работает</div>}
        </div>

        {problem && (
          <>
            <div className={Classes.DIALOG_BODY}>
              <div className={styles.text}>{problem}</div>
              <div className={styles.text}>
                Попросите администратора отправить новую ссылку или нажмите «Забыли пароль?» на странице входа.
              </div>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
              <PlanrButton type="blueish" size="small" onClick={() => navigate(routes.login)} style={{ width: "100%" }}>
                На страницу входа
              </PlanrButton>
            </div>
          </>
        )}

        {page && schema && (
          <Formik<InviteValues>
            initialValues={{ login: page.login ?? "", password: "", repeat: "" }}
            validationSchema={schema}
            onSubmit={async (values) => {
              const ok = await session.acceptAccessLink(token, invite ? values.login.trim() : "", values.password);
              if (ok) {
                navigate(routes.login);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form autoComplete="off">
                <div className={Classes.DIALOG_BODY}>
                  <StandardFormInput
                    name="login"
                    schema={schema}
                    small={true}
                    inline={false}
                    className="planr-form-input"
                  >
                    {({ field }) => (
                      <InputGroup
                        id={field.name}
                        {...field}
                        className="planr-default-input"
                        readOnly={!invite}
                        autoComplete="username"
                      />
                    )}
                  </StandardFormInput>

                  <StandardFormInput
                    name="password"
                    schema={schema}
                    small={true}
                    inline={false}
                    className="planr-form-input"
                  >
                    {({ field }) => (
                      <InputGroup
                        id={field.name}
                        {...field}
                        type="password"
                        className="planr-default-input"
                        autoComplete="new-password"
                      />
                    )}
                  </StandardFormInput>

                  <StandardFormInput
                    name="repeat"
                    schema={schema}
                    small={true}
                    inline={false}
                    className="planr-form-input"
                  >
                    {({ field }) => (
                      <InputGroup
                        id={field.name}
                        {...field}
                        type="password"
                        className="planr-default-input"
                        autoComplete="new-password"
                      />
                    )}
                  </StandardFormInput>
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                  <PlanrButton
                    type="blueish"
                    size="small"
                    nativeType="submit"
                    icon="general-sign-in"
                    disabled={isSubmitting}
                    style={{ width: "100%" }}
                  >
                    {invite ? "Сохранить и перейти ко входу" : "Сохранить пароль"}
                  </PlanrButton>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </Page>
  );
});
